/**
 * Where the image is, and whether there is one (engine target §8).
 *
 * The image is found FROM THE RAYS and the formula is the check, never the
 * other way round — so the bench has no way to report an image the light does
 * not actually form. Extend each ray's last segment to a full line, intersect
 * them pairwise, and the centroid is the image; how far the intersections
 * scatter (the spread) says whether there is a point image at all.
 */
import { fanAims, targetFor } from './construction';
import { formulaFor } from './convention';
import { defOf } from './registry';
import { traceRay } from './trace';
import { ImagePoint, ImageReport, Part, SampledImage, Surface, ThinElement, Vec2, World } from './types';
import { add, cross, dist, dot, scale, sub } from './vec';
import { ObjectData } from './kinds/sources';

/** Below this spread, cm, the rays meet at a point. */
export const POINT_TOL = 0.01;
const PROBES = 5;
/** Beyond this, cm, an image is at infinity for every purpose the bench has. */
const FAR = 1e7;

export interface PointImage extends ImagePoint {
    /** The parts the probes crossed, in order — the path the formula must follow. */
    path: string[];
}

interface Line { p: Vec2; d: Vec2 }

export function imageOfPoint(surfaces: Surface[], from: Vec2, el: ThinElement | null): PointImage {
    const none = (why: string, at: Vec2 = from): PointImage => ({ at, spread: 0, kind: 'none', why, path: [] });
    if (!el) return none('No lens or mirror is in front of it.');

    const traced = fanAims(from, el, PROBES, 0.9).map(d => traceRay(surfaces, from, d, { probe: true }));
    const usable = traced.filter(r => r.ended === 'left-the-bench' && r.vertices.length > 2);
    if (usable.length < 2) return none('Its light does not get through: every ray is blocked or misses.');

    // Rays that took different routes do not form one image; follow the route most took.
    const groups = new Map<string, typeof usable>();
    for (const r of usable) {
        const key = r.vertices.slice(1, -1).map(x => x.surfaceId).join('|');
        groups.set(key, [...(groups.get(key) ?? []), r]);
    }
    const best = [...groups.entries()].sort((a, b) => b[1].length - a[1].length || b[0].length - a[0].length)[0][1];
    if (best.length < 2) return none('Its rays take different routes, so they do not form one image.');

    const hits = best[0].vertices.slice(1, -1);
    const path = hits.map(x => x.partId as string).filter((id, i, all) => i === 0 || all[i - 1] !== id);
    const lines: Line[] = best.map(r => {
        const last = r.vertices[r.vertices.length - 2];
        return { p: last.at, d: last.direction };
    });

    return { ...meet(lines), path };
}

/** Where lines meet: a point, at infinity, or nowhere in particular. */
export function meet(lines: Line[]): ImagePoint {
    const points: Vec2[] = [];
    for (let i = 0; i < lines.length; i++) {
        for (let j = i + 1; j < lines.length; j++) {
            const c = cross(lines[i].d, lines[j].d);
            if (Math.abs(c) < 1e-12) continue;
            const s = cross(sub(lines[j].p, lines[i].p), lines[j].d) / c;
            points.push(add(lines[i].p, scale(lines[i].d, s)));
        }
    }
    if (points.length === 0) {
        return { at: lines[0].p, spread: 0, kind: 'at-infinity', direction: lines[0].d };
    }
    const at = scale(points.reduce(add, { x: 0, y: 0 }), 1 / points.length);
    const spread = Math.max(...points.map(q => dist(q, at)));
    const far = Math.max(...lines.map(l => dist(at, l.p)));
    if (far > FAR) return { at: lines[0].p, spread: 0, kind: 'at-infinity', direction: lines[0].d };

    const ahead = lines.reduce((s, l) => s + dot(sub(at, l.p), l.d), 0) / lines.length;
    if (spread > Math.max(POINT_TOL, 1e-9 * far)) {
        return {
            at, spread, kind: 'none',
            why: `These rays do not meet at a point — they cross over ${spread.toFixed(1)} cm.`,
        };
    }
    return { at, spread, kind: ahead > 0 ? 'real' : 'virtual' };
}

const isPoint = (k: ImagePoint['kind']): boolean => k === 'real' || k === 'virtual';

/**
 * Split sampled images into drawable pieces: consecutive points of the same
 * kind. An object across the focal plane gives two, each running off to infinity.
 */
export function piecesOf(points: SampledImage[]): { from: number; to: number }[] {
    const pieces: { from: number; to: number }[] = [];
    let start = -1;
    for (let i = 0; i <= points.length; i++) {
        const continues = i < points.length && isPoint(points[i].kind)
            && (start >= 0 && points[i].kind === points[start].kind);
        if (continues) continue;
        if (start >= 0 && i - 1 > start) pieces.push({ from: start, to: i - 1 });
        start = i < points.length && isPoint(points[i].kind) ? i : -1;
    }
    return pieces;
}

const MAX_SAMPLES = 200;

/** Samples are added where neighbouring images are far apart, relative to the piece. */
function refine(
    samples: SampledImage[], image: (t: number) => SampledImage,
): SampledImage[] {
    let out = samples;
    for (let pass = 0; pass < 3 && out.length < MAX_SAMPLES; pass++) {
        const pieces = piecesOf(out);
        const lengthOf = new Map<number, number>();
        for (const pc of pieces) {
            let L = 0;
            for (let i = pc.from; i < pc.to; i++) L += dist(out[i].at, out[i + 1].at);
            for (let i = pc.from; i <= pc.to; i++) lengthOf.set(i, L);
        }
        const next: SampledImage[] = [];
        let added = false;
        for (let i = 0; i < out.length; i++) {
            next.push(out[i]);
            const a = out[i], b = out[i + 1];
            if (!b || !lengthOf.has(i) || !lengthOf.has(i + 1) || a.kind !== b.kind) continue;
            if (dist(a.at, b.at) > 0.02 * (lengthOf.get(i) as number) && next.length < MAX_SAMPLES) {
                next.push(image((a.t + b.t) / 2));
                added = true;
            }
        }
        out = next;
        if (!added) break;
    }
    return out;
}

/**
 * The image of an object or point source, point by point (engine target §8.1),
 * with the formula's working when the formula applies.
 */
export function imageReport(w: World, part: Part, surfaces: Surface[], prefer?: string): ImageReport | null {
    const kind = defOf(part);
    if (!kind.points) return null;
    const pts = kind.points(part, w);
    const extended = pts.some(p => p.t !== undefined);
    const anchor = pts.find(p => p.t === 0.5) ?? pts[Math.floor(pts.length / 2)];
    const el = targetFor(w, surfaces, anchor.at, prefer);

    const at = (t: number): Vec2 => {
        const exact = pts.find(p => p.t === t);
        if (exact) return exact.at;
        const a = pts[0].at, b = pts[pts.length - 1].at;
        return add(a, scale(sub(b, a), t));
    };
    const image = (t: number): SampledImage => {
        const o = at(t);
        const { path: _path, ...img } = imageOfPoint(surfaces, o, el);
        return { ...img, t, object: o };
    };

    let points: SampledImage[] = pts.map(p => image(p.t ?? 0));
    if (extended) points = refine(points, image);

    const end = (t: number): SampledImage | undefined => points.find(p => p.t === t);
    const A = end(0) ?? points[0];
    const B = end(1) ?? points[points.length - 1];
    const lead = extended ? B : A;

    const leadPath = imageOfPoint(surfaces, lead.object, el).path;
    const formula = formulaFor(w, part, leadPath, lead.object.x);

    const tilt = part.kind === 'object' ? (part.data as ObjectData).tilt : 0;
    const transverse = extended && Math.abs(tilt) < 1e-9;
    const report: ImageReport = {
        partId: part.id,
        points,
        pieces: piecesOf(points),
        transverse,
        stages: formula.stages,
        working: formula.working,
        applies: formula.applies,
        ...(formula.why ? { why: formula.why } : {}),
    };

    if (extended && isPoint(A.kind) && isPoint(B.kind)) {
        const dh = B.object.y - A.object.y;
        const dy = B.at.y - A.at.y;
        if (transverse) {
            report.height = dy;
            report.magnification = dy / dh;
            report.erect = dy / dh > 0;
        } else {
            report.erect = Math.abs(dh) > 1e-9 ? dy * dh > 0 : (B.at.x - A.at.x) * (B.object.x - A.object.x) > 0;
        }
    }
    if (extended && !transverse && formula.applies) {
        const mA = formulaFor(w, part, leadPath, A.object.x).m;
        const mB = formula.m;
        if (mA !== undefined && mB !== undefined) report.endMagnification = { a: mA, b: mB };
        if (Math.abs(Math.abs(tilt) - 90) < 1e-9 && isPoint(A.kind) && isPoint(B.kind)) {
            report.longitudinal = (B.at.x - A.at.x) / (B.object.x - A.object.x);
        }
    }
    return report;
}
